import { Callout, Card, TextField } from "@bruntworkwear-apps/ui";
import styles from "../UploadForm.module.css";
import { BbWcTogglePair } from "./BbWcTogglePair.view";
import { RateBandsView } from "./RateBands.view";
import type { RateBandsState } from "../useRateBands";

interface BbWcRatesProps {
  rateFallback: boolean;
  rateAlways: boolean;
  landingRate: string;
  landingRateError: string | null;
  rateBands: RateBandsState;
  customsFallback: boolean;
  customsAlways: boolean;
  customsRate: string;
  customsRateError: string | null;
  disabled: boolean;
  onRateFallback: (value: boolean) => void;
  onRateAlways: (value: boolean) => void;
  onLandingRate: (value: string) => void;
  onCustomsFallback: (value: boolean) => void;
  onCustomsAlways: (value: boolean) => void;
  onCustomsRate: (value: string) => void;
}

/**
 * The optional Boot Barn wholesale landing and customs rates.
 *
 * BB WC Shipments clears through a different port of entry than the rest of
 * the tracker, so its lines are landed and duty-rated separately. Left alone,
 * they take the freight and duty actually invoiced on that tab, exactly as
 * every other order, and are left at zero and flagged when the forwarder has
 * not billed yet. This card lets the accountant supply a quoted rate instead,
 * for either figure or both.
 *
 * "Always" strictly contains "fallback": that coupling lives in
 * `useUploadForm.ts`'s setters, not here. Landing and customs are independent
 * pairs - the accountant may want a quoted rate for one and not the other.
 *
 * @example
 * <BbWcRates rateFallback={false} rateAlways={false} landingRate="" landingRateError={null}
 *   rateBands={bbWcBands} customsFallback={false} customsAlways={false} customsRate=""
 *   customsRateError={null} disabled={false} onRateFallback={setRateFallback}
 *   onRateAlways={setRateAlways} onLandingRate={setLandingRate}
 *   onCustomsFallback={setCustomsFallback} onCustomsAlways={setCustomsAlways}
 *   onCustomsRate={setCustomsRate} />
 */
export function BbWcRates(props: BbWcRatesProps) {
  const {
    rateFallback,
    rateAlways,
    landingRate,
    landingRateError,
    rateBands,
    customsFallback,
    customsAlways,
    customsRate,
    customsRateError,
    disabled,
    onRateFallback,
    onRateAlways,
    onLandingRate,
    onCustomsFallback,
    onCustomsAlways,
    onCustomsRate,
  } = props;

  const landingOn = rateFallback || rateAlways;
  const customsOn = customsFallback || customsAlways;

  return (
    <Card title="Boot Barn wholesale">
      <div className={styles.bbWc}>
        <p className={styles.bbWcHint}>
          BB WC Shipments clears through a different port of entry. Left alone, its lines are
          landed and duty-rated at the freight and duty actually invoiced on that tab, same as
          every other order - and left at zero and flagged when the forwarder has not billed yet.
        </p>

        <div className={styles.bbWcSection}>
          <BbWcTogglePair
            legend="Landing rate"
            fallbackLabel="Use a quoted rate when the forwarder has not invoiced freight yet"
            alwaysLabel="Always use the quoted rate, even over an invoiced figure"
            fallback={rateFallback}
            always={rateAlways}
            disabled={disabled}
            onFallback={onRateFallback}
            onAlways={onRateAlways}
          />

          <TextField
            label="Boot Barn wholesale landing rate"
            prefix="$"
            inputMode="decimal"
            value={landingRate}
            error={landingRateError}
            hint="Per unit, applied wherever no dated band below covers the line."
            onChange={(event) => onLandingRate(event.target.value)}
            disabled={disabled || !landingOn}
          />

          {landingOn && (
            <div className={styles.bbWcBands}>
              <RateBandsView bands={rateBands} flatRate={landingRate} disabled={disabled} />
            </div>
          )}
        </div>

        <div className={styles.bbWcSection}>
          <BbWcTogglePair
            legend="Customs rate"
            fallbackLabel="Use a quoted duty rate when the forwarder has not invoiced customs yet"
            alwaysLabel="Always use the quoted duty rate, even over an invoiced figure"
            fallback={customsFallback}
            always={customsAlways}
            disabled={disabled}
            onFallback={onCustomsFallback}
            onAlways={onCustomsAlways}
          />

          <TextField
            label="Boot Barn wholesale customs rate"
            prefix="$"
            inputMode="decimal"
            value={customsRate}
            error={customsRateError}
            hint="Per unit duty, multiplied by quantity exactly like the landing rate."
            onChange={(event) => onCustomsRate(event.target.value)}
            disabled={disabled || !customsOn}
          />
        </div>

        {(landingOn || customsOn) && (
          <Callout tone="warning">
            The Boot Barn wholesale accrual becomes an estimate against a quote rather than the
            figure actually invoiced. The choice is recorded on the Parameters sheet of the
            export.
          </Callout>
        )}
      </div>
    </Card>
  );
}
